import { TeBadge, TeContainerSlim, TeIcon, TeText } from '@topcon/tuicl-react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { memo, useMemo } from 'react';

import IconWrapper from '../../IconWrapper';
import { border } from '../../../providers/theme/theme.index';
import { buildColumns, getAgreementTypeText, getStatusBadge } from './ServiceAgreementRowHelper';
import CustomBadge from '../../common/CustomBadge/CustomBadge';
import { ServiceAgreement } from './ServiceAgreementTypes';
import {
  formatOrganizationNumber,
  stripOrganizationNumberFormatting,
} from '../../../utils/organizationNumberFormatter';
import { ServiceAgreementStatusEnum } from '../../../api/generated/pm';
import { useAppService } from '../../../providers/app/app.context';
import { UserRole } from '../../../types';
import { BREAKPOINTS } from '../../../utils/breakpoints';
import { useMediaQuery } from '../../../hooks/useMediaQuery';

const ACTION_COLUMN_KEYS = new Set(['actions']);

export type ServiceAgreementRowProps = {
  sa: ServiceAgreement;

  onReview?: (sa: ServiceAgreement) => void;
  onCancelRequest?: (sa: ServiceAgreement) => void;
  onDeleteRequest?: (sa: ServiceAgreement) => void;
  onSupportCustomer?: (sa: ServiceAgreement) => void;
};

function ServiceAgreementRowBase({
  sa,
  onReview,
  onCancelRequest,
  onDeleteRequest,
  onSupportCustomer,
}: ServiceAgreementRowProps) {
  const { t } = useTranslation();
  const { userRoles, loadedRoles } = useAppService();
  const isTablet = useMediaQuery(`(max-width: ${BREAKPOINTS.tablet})`);
  const isCompactTablet = useMediaQuery(
    `(${BREAKPOINTS.mobile} <= width <= ${BREAKPOINTS.tablet})`
  );

  const hasSupportRole = useMemo(() => {
    return loadedRoles && userRoles.some((role) => role === UserRole.ServiceCustomerAdministrator);
  }, [loadedRoles, userRoles]);

  const user = sa.provider;
  const extId = formatOrganizationNumber(stripOrganizationNumberFormatting(user?.extId || ''));
  const name = user?.name ?? '-';
  const address = [
    user?.companyAddress?.addressLineOne ?? user?.companyAddress?.addressLineTwo,
    user?.companyAddress?.city,
    user?.companyAddress?.stateProvinceRegion,
    user?.companyAddress?.country,
  ]
    .filter(Boolean)
    .join(', ');

  const status = sa.computedServiceAgreementStatus;
  const statusBadge = getStatusBadge(status, t);
  const agreementTypeText = getAgreementTypeText(sa.serviceAgreementTerms, t);

  const columns = buildColumns({
    sa,
    t,
    hasSupportRole,
    isTablet,
    isCompactTablet,
    onReview,
    onCancelRequest,
    onDeleteRequest,
    onSupportCustomer,
  });

  const dataColumns = columns.filter((column) => !ACTION_COLUMN_KEYS.has(column.key));
  const actionColumns = columns.filter((column) => ACTION_COLUMN_KEYS.has(column.key));

  return (
    <TeContainerSlim
      fill
      grow
      backgroundColor="background.secondary"
      padding="m"
      borderRadius="l"
      borderColor="border.secondary"
    >
      <Layout>
        <TeContainerSlim alignItems="flex-start" gap="s" grow>
          <IconWrapper>
            <TeIcon name="business" size="xl" />
          </IconWrapper>
          <TeContainerSlim column gap="xs2" fill>
            <TeContainerSlim gap="xs" alignItems="center" wrap>
              <TeText size={16} weight={500}>
                {name}
              </TeText>
              {extId &&
                (status === ServiceAgreementStatusEnum.ACTIVE ||
                  status === ServiceAgreementStatusEnum.PENDING) && (
                  <CustomBadge
                    borderColor="border.secondary"
                    backgroundColor="background.tertiary"
                    textColor="text.primary"
                    text={extId}
                    size="s"
                  />
                )}
            </TeContainerSlim>
            <TeText color="text.secondary" weight={400} size={12}>
              {address}
            </TeText>
          </TeContainerSlim>
        </TeContainerSlim>

        <Details>
          <MetaGrid>
            <MetaCell column padding={[0, 's']}>
              <TeContainerSlim column gap="xs" justifyContent="space-between">
                <TeText weight={400} size={12} color="text.secondary">
                  {t('status', { ns: 'users' })}
                </TeText>
                <CustomBadge
                  backgroundColor={statusBadge.bg}
                  textColor={statusBadge.textColor}
                  text={statusBadge.text}
                  borderColor={statusBadge.borderColor}
                  size="s"
                />
              </TeContainerSlim>
            </MetaCell>

            <MetaCell column padding={[0, 's']} alignItems="flex-start">
              <TeContainerSlim column gap="xs" justifyContent="space-between">
                <TeText weight={400} size={12} color="text.secondary">
                  {t('dealers.agreementType')}
                </TeText>
                <TeBadge backgroundColor="background.inverse" text={agreementTypeText} size="s" />
              </TeContainerSlim>
            </MetaCell>

            {dataColumns.map((column) => (
              <MetaCell key={column.key} column padding={[0, 's']} alignItems="flex-start">
                {column.render()}
              </MetaCell>
            ))}
          </MetaGrid>

          {actionColumns.length > 0 && (
            <Actions>
              {actionColumns.map((column) => (
                <ActionCell key={column.key}>{column.render()}</ActionCell>
              ))}
            </Actions>
          )}
        </Details>
      </Layout>
    </TeContainerSlim>
  );
}

const ServiceAgreementRow = memo(ServiceAgreementRowBase);
export default ServiceAgreementRow;

const Layout = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  width: 100%;
  align-items: center;
  gap: 0;

  @media (max-width: ${BREAKPOINTS.tablet}) {
    grid-template-columns: 1fr;
    align-items: stretch;
    gap: 16px;
  }
`;

const Details = styled.div`
  display: flex;
  align-items: center;
  min-width: 0;

  @media (max-width: ${BREAKPOINTS.tablet}) {
    flex-direction: column;
    align-items: stretch;
    gap: 16px;
    width: 100%;
  }
`;

const MetaGrid = styled.div`
  display: flex;
  align-items: flex-start;
  min-width: 0;

  @media (max-width: ${BREAKPOINTS.tablet}) {
    display: grid;
    grid-template-columns: 1fr 1fr;
    row-gap: 12px;
    width: 100%;
  }
`;

const MetaCell = styled(TeContainerSlim)`
  position: relative;
  height: 100% !important;
  min-height: 40px !important;
  min-width: 130px !important;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: 1px;
    background-color: ${border.primary};
  }

  @media (max-width: ${BREAKPOINTS.tablet}) {
    min-width: 0 !important;
    min-height: 0 !important;
    height: auto !important;
    padding: 0 12px !important;

    &:nth-child(odd) {
      padding-left: 0 !important;

      &:before {
        display: none;
      }
    }

    &:nth-child(even) {
      padding-right: 0 !important;
    }
  }
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  position: relative;
  min-height: 40px;
  padding: 0 12px;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: 1px;
    background-color: ${border.primary};
  }

  @media (max-width: ${BREAKPOINTS.tablet}) {
    width: 100%;
    min-height: 0;
    padding: 0;
    gap: 8px;

    &:before {
      display: none;
    }
  }
`;

const ActionCell = styled.div`
  display: flex;
  align-items: center;

  @media (max-width: ${BREAKPOINTS.tablet}) {
    width: 100%;

    & > * {
      width: 100%;
    }
  }
`;
